import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/marketing/blocks";

const PRICING_CARDS = [
  { key: "routine", name: "Routine Process Service", price: "Starting at $95", note: "First attempt within 3–5 business days", bullets: ["Up to 4 attempts (morning, afternoon, evening and weekend), completed within 10 business days"] },
  { key: "rush", name: "Rush Process Service", price: "Starting at $135", note: "First attempt within 48 hours", bullets: ["Up to 4 attempts at varied times, completed faster than routine"] },
  { key: "sameday", name: "24-Hour Priority Service", price: "Starting at $285", note: "First attempt within 24 hours of receipt", bullets: ["Up to 4 attempts at varied times. Subject to availability and document review."] },
  { key: "notary", name: "Mobile notary", price: "$35 travel fee", note: "Plus applicable notarial and service fees.", bullets: [] },
];

// `cards` renders the heading, intro and four pricing cards. `disclaimer` renders the
// "Timeframes start when complete…" disclosure paragraph. Both default to true so the
// homepage keeps rendering the full section unchanged.
export function PricingSection({ cards = true, disclaimer = true }) {
  return (
    <section
      className="border-b border-slate-200 bg-slate-50 px-5 py-14 sm:px-8 lg:py-20"
      data-testid={cards ? "landing-pricing-section" : "landing-pricing-disclaimer-section"}
    >
      <div className="mx-auto max-w-5xl">
        {cards && (
          <>
            <SectionHeading
              overline="Pricing"
              title="Memphis and Shelby County pricing"
              subtitle="Process serving starts at $95. Choose routine service or request a faster first attempt when your deadline requires it."
            />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PRICING_CARDS.map((c) => (
                <Link
                  key={c.key}
                  to="/login?mode=register"
                  data-testid={`landing-pricing-card-${c.key}`}
                  className="flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition hover:border-sky-400 hover:shadow-md"
                >
                  <h3 className="text-[15px] font-semibold text-slate-900">{c.name}</h3>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{c.price}</p>
                  {c.note && <p className="mt-2 text-sm text-slate-500">{c.note}</p>}
                  {c.bullets.length > 0 && (
                    <>
                      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">What you get</p>
                      <ul className="mt-2 space-y-2">
                        {c.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700">
                    Get started <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}
        {disclaimer && (
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-500" data-testid="landing-pricing-disclaimer">
            Timeframes start when complete, ready-to-serve documents and payment are received. Business days are Monday–Friday, excluding holidays. We confirm the included attempts, first-attempt timing, scope and total price before accepting your order. Rush and 24-Hour Priority options refer to the timing of the first attempt. Successful service is not guaranteed. Additional defendants, addresses, attempts, skip tracing and other requested services may cost extra. Any applicable additional charges will be disclosed in your quote. Additional attempts beyond those included may be quoted at an added charge.
          </p>
        )}
      </div>
    </section>
  );
}
